import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Empty,
  InputNumber,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from 'antd';
import { InboxOutlined, PauseCircleOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

import { fetchAuthSession } from '../auth/gateway';
import AudioWaveform from '../components/AudioWaveform';
import { API_CONFIG } from '../config/api';
import { fetchSignedMediaUrl } from '../utils/media';
import { secureLogger } from '../utils/secureLogger';

const { Dragger } = Upload;
const { Paragraph, Text, Title } = Typography;

type PlaygroundStatus = 'idle' | 'submitted' | 'ready';
type BoundaryMergeGeminiModel = 'gemini-2.5-flash' | 'gemini-2.5-flash-lite';

interface AsrPlaygroundResponse {
  status: PlaygroundStatus;
  filename?: string | null;
  submitted_at?: string | null;
  job_id?: string | null;
  result_ready: boolean;
  job_meta?: Record<string, unknown> | null;
  segments: Array<Record<string, unknown>>;
  boundary_debug_uri?: string | null;
  clustering_debug_uri?: string | null;
  boundary_debug?: Record<string, unknown> | null;
  clustering_debug?: Record<string, unknown> | null;
}

interface BoundaryDebugEntry {
  left_index?: number;
  right_index?: number;
  gap_ms?: number;
  rule_score?: number;
  decision?: string;
  decision_source?: string;
  decision_input?: Record<string, unknown> | null;
  decision_rationale?: string;
  latency_ms?: number;
  gemini_confidence?: number;
  gemini?: Record<string, unknown> | null;
  left_text?: string;
  right_text?: string;
}

interface ClusterSummaryEntry {
  label?: string;
  members?: Array<number | string>;
}

interface ClusterSegmentEntry {
  index?: number;
  final_label?: string;
  pre_label?: string;
  auto_label?: string;
}

interface TranscriptSegmentLine {
  key: string;
  speaker: string;
  text: string;
  startSec: number;
}

const formatJst = (value?: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
};

const extractSegmentText = (segment: Record<string, unknown>): string => {
  const text = segment.text;
  return typeof text === 'string' ? text : '';
};

const extractSegmentSpeaker = (segment: Record<string, unknown>): string => {
  const speaker = segment.speaker;
  return typeof speaker === 'string' && speaker.trim() ? speaker : '話者不明';
};

const extractSegmentStartSec = (segment: Record<string, unknown>): number => {
  const startMs = segment.start_ms;
  if (typeof startMs === 'number' && Number.isFinite(startMs)) {
    return Math.max(0, startMs / 1000);
  }
  const startSec = segment.start_sec ?? segment.start;
  if (typeof startSec === 'number' && Number.isFinite(startSec)) {
    return Math.max(0, startSec);
  }
  return 0;
};

const formatPlaybackTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return '--:--';
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${mm}:${ss}`;
};

const toObject = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const toBoundaryEntries = (value: Record<string, unknown> | null | undefined): BoundaryDebugEntry[] => {
  const boundaries = value?.boundaries;
  if (!Array.isArray(boundaries)) {
    return [];
  }
  return boundaries.filter((entry): entry is BoundaryDebugEntry => !!entry && typeof entry === 'object');
};

const toClusterSummary = (value: Record<string, unknown> | null | undefined): ClusterSummaryEntry[] => {
  const clusters = value?.clusters;
  if (Array.isArray(clusters)) {
    return clusters.filter((entry): entry is ClusterSummaryEntry => !!entry && typeof entry === 'object');
  }

  const clusterMap = toObject(value?.cluster_summary);
  if (!clusterMap) {
    return [];
  }

  return Object.entries(clusterMap).map(([label, members]) => ({
    label,
    members: Array.isArray(members) ? members.filter((member) => ['number', 'string'].includes(typeof member)) : [],
  }));
};

const toClusterSegments = (value: Record<string, unknown> | null | undefined): ClusterSegmentEntry[] => {
  const segments = value?.segments;
  if (!Array.isArray(segments)) {
    return [];
  }
  return segments.filter((entry): entry is ClusterSegmentEntry => !!entry && typeof entry === 'object');
};

const formatPrimitive = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? `${value}` : value.toFixed(2);
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
};

const renderInlineJsonCollapse = (label: string, value: unknown): React.ReactNode => {
  const objectValue = toObject(value);
  if (!objectValue) {
    return '-';
  }

  return (
    <Collapse
      size="small"
      items={[
        {
          key: label,
          label,
          children: (
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(objectValue, null, 2)}
            </pre>
          ),
        },
      ]}
    />
  );
};

const extractBoundaryLatencyMs = (entry: BoundaryDebugEntry): number | null => {
  if (typeof entry.latency_ms === 'number' && Number.isFinite(entry.latency_ms)) {
    return entry.latency_ms;
  }
  const gemini = toObject(entry.gemini);
  const geminiLatencyMs = gemini?.latency_ms;
  if (typeof geminiLatencyMs === 'number' && Number.isFinite(geminiLatencyMs)) {
    return geminiLatencyMs;
  }
  return null;
};

const AsrPlaygroundPage: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<AsrPlaygroundResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [expectedSpeakerCount, setExpectedSpeakerCount] = useState<number | null>(null);
  const [boundaryMergeGeminiModel, setBoundaryMergeGeminiModel] = useState<BoundaryMergeGeminiModel | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const fetchLatestResult = useCallback(async () => {
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('Cognito access token を取得できませんでした。');
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/asr-playground`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`ASR Playground の取得に失敗しました: ${response.status}`);
      }

      const data = (await response.json()) as AsrPlaygroundResponse;
      setResult(data);
      setErrorMessage(null);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'ASR Playground の取得に失敗しました。';
      secureLogger.error('Failed to load ASR playground result', { error: messageText });
      setErrorMessage(messageText);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLatestResult();
  }, [fetchLatestResult]);

  useEffect(() => {
    if (result?.status !== 'submitted') {
      return;
    }

    const timer = window.setInterval(() => {
      void fetchLatestResult();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [fetchLatestResult, result?.status]);

  useEffect(() => {
    let cancelled = false;

    const loadAudioUrl = async () => {
      if (!result || result.status === 'idle') {
        setAudioUrl(null);
        setAudioDuration(0);
        setCurrentTime(0);
        setIsPlaying(false);
        return;
      }

      setAudioLoading(true);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          throw new Error('Cognito access token を取得できませんでした。');
        }
        const signedUrl = await fetchSignedMediaUrl('/api/v1/internal/asr-playground/file', token);
        if (!cancelled) {
          setAudioUrl(signedUrl);
          setCurrentTime(0);
          setIsPlaying(false);
        }
      } catch (error) {
        const messageText =
          error instanceof Error ? error.message : 'ASR Playground 音声の読み込みに失敗しました。';
        secureLogger.error('Failed to load ASR playground audio', { error: messageText });
        if (!cancelled) {
          setAudioUrl(null);
        }
      } finally {
        if (!cancelled) {
          setAudioLoading(false);
        }
      }
    };

    void loadAudioUrl();

    return () => {
      cancelled = true;
    };
  }, [result?.job_id, result?.status]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const handleLoadedMetadata = () => {
      if (Number.isFinite(audio.duration)) {
        setAudioDuration(audio.duration);
      }
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl]);

  const submitFile = useCallback(async () => {
    const candidate = fileList[0]?.originFileObj;
    if (!candidate) {
      message.warning('先に音声ファイルを選んでよね。');
      return;
    }

    setSubmitting(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('Cognito access token を取得できませんでした。');
      }

      const formData = new FormData();
      formData.append('file', candidate);
      if (expectedSpeakerCount !== null) {
        formData.append('expected_speaker_count', String(expectedSpeakerCount));
      }
      if (boundaryMergeGeminiModel) {
        formData.append('boundary_merge_gemini_model', boundaryMergeGeminiModel);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/asr-playground`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(`ASR Playground の投入に失敗しました: ${response.status} ${detail}`);
      }

      message.success('ASR Playground に投入したよ。少し待って結果を見な。');
      await fetchLatestResult();
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'ASR Playground の投入に失敗しました。';
      secureLogger.error('Failed to submit ASR playground job', { error: messageText });
      setErrorMessage(messageText);
    } finally {
      setSubmitting(false);
    }
  }, [boundaryMergeGeminiModel, expectedSpeakerCount, fetchLatestResult, fileList]);

  const transcriptLines = useMemo<TranscriptSegmentLine[]>(() => {
    if (!result?.segments?.length) {
      return [];
    }

    return result.segments
      .map((segment, index) => {
        const text = extractSegmentText(segment);
        if (!text) {
          return null;
        }
        return {
          key: `${segment.segment_id ?? index}`,
          speaker: extractSegmentSpeaker(segment),
          text,
          startSec: extractSegmentStartSec(segment),
        };
      })
      .filter((segment): segment is TranscriptSegmentLine => Boolean(segment));
  }, [result]);

  const boundaryEntries = useMemo(() => toBoundaryEntries(result?.boundary_debug), [result]);
  const clusteringSummary = useMemo(() => toClusterSummary(result?.clustering_debug), [result]);
  const clusteringSegments = useMemo(() => toClusterSegments(result?.clustering_debug), [result]);
  const activeTranscriptKey = useMemo(() => {
    if (!transcriptLines.length) {
      return null;
    }
    for (let index = 0; index < transcriptLines.length; index += 1) {
      const current = transcriptLines[index];
      const next = transcriptLines[index + 1];
      const nextStart = next?.startSec ?? Number.POSITIVE_INFINITY;
      if (currentTime >= current.startSec && currentTime < nextStart) {
        return current.key;
      }
    }
    return currentTime < transcriptLines[0].startSec ? null : transcriptLines[transcriptLines.length - 1].key;
  }, [currentTime, transcriptLines]);

  const handleTogglePlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return;
    }

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        await audio.play();
        setIsPlaying(true);
      }
    } catch (error) {
      secureLogger.error('Failed to toggle ASR playground audio playback', {
        error: error instanceof Error ? error.message : 'unknown',
      });
    }
  }, [audioUrl, isPlaying]);

  const handleSeek = useCallback((nextTime: number, shouldPlay = false) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    const clampedTime = Math.max(0, Math.min(audioDuration || Number.MAX_SAFE_INTEGER, nextTime));
    audio.currentTime = clampedTime;
    setCurrentTime(clampedTime);
    if (shouldPlay) {
      void audio.play().then(() => setIsPlaying(true)).catch((error) => {
        secureLogger.error('Failed to start ASR playground audio playback after seek', {
          error: error instanceof Error ? error.message : 'unknown',
        });
      });
      return;
    }
    audio.pause();
    setIsPlaying(false);
  }, [audioDuration]);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>
            ASR Playground
          </Title>
          <Paragraph style={{ marginBottom: 0 }}>
            ローカル音声ファイルを本番寄りの SQS 経路で batch-asr に投げる開発用ページだよ。
            正式な <Text code>audio</Text> や <Text code>audioSeries</Text> には保存しない。
          </Paragraph>
          <Text type="secondary">
            ユーザーごとの latest スロットを上書きする仕様。短時間に連打すると前の結果を踏み潰すことがあるから、そこはよわよわ運用しないで。
          </Text>
        </Space>
      </Card>

      {errorMessage && (
        <Alert type="error" showIcon message="ASR Playground でエラー" description={errorMessage} />
      )}

      <Card
        title="音声ファイルを投入"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void fetchLatestResult()} disabled={loading}>
              最新結果を再取得
            </Button>
            <Button type="primary" onClick={() => void submitFile()} loading={submitting}>
              batch-asr に投入
            </Button>
          </Space>
        }
      >
        <Dragger
          multiple={false}
          accept=".aac,.m4a,.wav,.mp3,.ogg,.flac"
          beforeUpload={() => false}
          fileList={fileList}
          onChange={({ fileList: nextFileList }) => setFileList(nextFileList.slice(-1))}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">音声ファイルをここにドロップ、またはクリックして選択</p>
          <p className="ant-upload-hint">AAC / M4A / WAV / MP3 / OGG / FLAC を想定</p>
        </Dragger>
        <Space direction="vertical" size={4} style={{ marginTop: 16, width: '100%' }}>
          <Text strong>想定話者数</Text>
          <InputNumber
            min={1}
            precision={0}
            value={expectedSpeakerCount}
            onChange={(value) => setExpectedSpeakerCount(typeof value === 'number' ? value : null)}
            placeholder="未入力なら送らない"
            style={{ width: 240 }}
          />
          <Text type="secondary">音声全体にいる想定話者数（登録済み話者を含む）。未入力なら従来どおり上限なし。</Text>
        </Space>
        <Space direction="vertical" size={4} style={{ marginTop: 16, width: '100%' }}>
          <Text strong>Boundary merge Gemini モデル</Text>
          <Select<BoundaryMergeGeminiModel>
            allowClear
            placeholder="未入力なら runtime 設定を使う"
            value={boundaryMergeGeminiModel ?? undefined}
            onChange={(value) => setBoundaryMergeGeminiModel(value ?? null)}
            style={{ width: 280 }}
            options={[
              { value: 'gemini-2.5-flash', label: 'gemini-2.5-flash' },
              { value: 'gemini-2.5-flash-lite', label: 'gemini-2.5-flash-lite' },
            ]}
          />
          <Text type="secondary">
            playground から boundary merge 用の Gemini モデルだけ上書きできる。未入力なら backend の runtime 設定値を使う。
          </Text>
        </Space>
      </Card>

      <Card title="最新の結果">
        {loading ? (
          <Space align="center">
            <Spin />
            <Text>最新結果を読んでる...</Text>
          </Space>
        ) : !result || result.status === 'idle' ? (
          <Empty description="まだ Playground 実行はないよ" />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="状態">
                {result.status === 'ready' ? '結果あり' : '投入済み'}
              </Descriptions.Item>
              <Descriptions.Item label="ファイル名">{result.filename || '-'}</Descriptions.Item>
              <Descriptions.Item label="投入時刻">{formatJst(result.submitted_at)}</Descriptions.Item>
              <Descriptions.Item label="job_id">{result.job_id || '-'}</Descriptions.Item>
            </Descriptions>

            {result.result_ready ? (
              <Card size="small" title={`segments.jsonl (${result.segments.length} segments)`}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {transcriptLines.length > 0 ? (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      {transcriptLines.map((segment) => {
                        const isActive = activeTranscriptKey === segment.key;
                        return (
                          <button
                            key={segment.key}
                            type="button"
                            onClick={() => handleSeek(segment.startSec)}
                            style={{
                              width: '100%',
                              display: 'block',
                              padding: 12,
                              textAlign: 'left',
                              background: isActive ? '#e6f4ff' : '#fafafa',
                              color: 'inherit',
                              borderRadius: 8,
                              border: isActive ? '1px solid #91caff' : '1px solid #f0f0f0',
                              cursor: 'pointer',
                              appearance: 'none',
                            }}
                          >
                            <div style={{ width: '100%' }}>
                              <div
                                style={{
                                  color: '#1677ff',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  lineHeight: 1.5,
                                  marginBottom: 4,
                                }}
                              >
                                [{segment.speaker}] {formatPlaybackTime(segment.startSec)}
                              </div>
                              <div
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.6,
                                }}
                              >
                                {segment.text}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </Space>
                  ) : (
                    <Text type="secondary">テキストはまだ無いみたい。</Text>
                  )}
                  <div style={{ width: '100%' }}>
                    <audio key={audioUrl ?? 'asr-playground-audio'} ref={audioRef} src={audioUrl || undefined} preload="metadata" />
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space size={8} wrap>
                        <Button
                          size="small"
                          type="primary"
                          shape="circle"
                          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                          onClick={() => void handleTogglePlayback()}
                          loading={audioLoading}
                          disabled={!audioUrl}
                        />
                        <Text type="secondary">
                          {formatPlaybackTime(currentTime)} / {formatPlaybackTime(audioDuration)}
                        </Text>
                      </Space>
                      {audioUrl ? (
                        <AudioWaveform
                          audioUrl={audioUrl}
                          currentTime={currentTime}
                          duration={audioDuration}
                          onSeek={(nextTime) => handleSeek(nextTime, isPlaying)}
                        />
                      ) : (
                        <Text type="secondary">音声再生の準備中だよ。少し待って。</Text>
                      )}
                    </Space>
                  </div>
                  <Collapse
                    size="small"
                    items={[
                      {
                        key: 'segments-raw',
                        label: 'segments.jsonl の生 JSON を表示',
                        children: (
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(result.segments, null, 2)}
                          </pre>
                        ),
                      },
                    ]}
                  />
                </Space>
              </Card>
            ) : (
              <Alert
                type="info"
                showIcon
                message="まだ結果は出てない"
                description="途中の Batch status は見せない仕様。少し待ってから再取得して。"
              />
            )}

            {(result.boundary_debug_uri || boundaryEntries.length > 0) && (
              <Card
                size="small"
                title={`boundary debug (${boundaryEntries.length} boundaries)`}
                extra={
                  result.boundary_debug_uri ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {result.boundary_debug_uri}
                    </Text>
                  ) : null
                }
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {boundaryEntries.length > 0 ? (
                    boundaryEntries.map((entry, index) => (
                      <Card
                        key={`${entry.left_index ?? index}-${entry.right_index ?? index}`}
                        size="small"
                        title={`boundary ${formatPrimitive(entry.left_index)} -> ${formatPrimitive(entry.right_index)}`}
                      >
                        <Descriptions bordered size="small" column={1}>
                          <Descriptions.Item label="gap_ms">{formatPrimitive(entry.gap_ms)}</Descriptions.Item>
                          <Descriptions.Item label="rule_score">{formatPrimitive(entry.rule_score)}</Descriptions.Item>
                          <Descriptions.Item label="decision">{formatPrimitive(entry.decision)}</Descriptions.Item>
                          <Descriptions.Item label="decision_source">
                            {formatPrimitive(entry.decision_source)}
                          </Descriptions.Item>
                          <Descriptions.Item label="decision_input">
                            {renderInlineJsonCollapse('クリックして表示', entry.decision_input)}
                          </Descriptions.Item>
                          <Descriptions.Item label="decision_rationale">
                            {formatPrimitive(entry.decision_rationale)}
                          </Descriptions.Item>
                          <Descriptions.Item label="latency_ms">
                            {formatPrimitive(extractBoundaryLatencyMs(entry))}
                          </Descriptions.Item>
                          <Descriptions.Item label="gemini_confidence">
                            {formatPrimitive(entry.gemini_confidence)}
                          </Descriptions.Item>
                          <Descriptions.Item label="left_text">{formatPrimitive(entry.left_text)}</Descriptions.Item>
                          <Descriptions.Item label="right_text">{formatPrimitive(entry.right_text)}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                    ))
                  ) : (
                    <Text type="secondary">debug URI はあるけど、boundary list は空みたい。</Text>
                  )}
                  {result.boundary_debug && (
                    <Collapse
                      size="small"
                      items={[
                        {
                          key: 'boundary-debug-raw',
                          label: '生 JSON を表示',
                          children: (
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(result.boundary_debug, null, 2)}
                            </pre>
                          ),
                        },
                      ]}
                    />
                  )}
                </Space>
              </Card>
            )}

            {(result.clustering_debug_uri || clusteringSummary.length > 0 || clusteringSegments.length > 0) && (
              <Card
                size="small"
                title="clustering debug"
                extra={
                  result.clustering_debug_uri ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {result.clustering_debug_uri}
                    </Text>
                  ) : null
                }
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {clusteringSummary.length > 0 && (
                    <Card size="small" title={`cluster summary (${clusteringSummary.length} clusters)`}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {clusteringSummary.map((cluster, index) => (
                          <Descriptions key={`${cluster.label ?? 'cluster'}-${index}`} bordered size="small" column={1}>
                            <Descriptions.Item label="label">{formatPrimitive(cluster.label)}</Descriptions.Item>
                            <Descriptions.Item label="members">
                              {Array.isArray(cluster.members) && cluster.members.length > 0
                                ? cluster.members.join(', ')
                                : '-'}
                            </Descriptions.Item>
                          </Descriptions>
                        ))}
                      </Space>
                    </Card>
                  )}

                  {clusteringSegments.length > 0 && (
                    <Card size="small" title={`segment labels (${clusteringSegments.length} segments)`}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {clusteringSegments.map((segment, index) => (
                          <Descriptions key={`${segment.index ?? index}`} bordered size="small" column={1}>
                            <Descriptions.Item label="segment_index">{formatPrimitive(segment.index)}</Descriptions.Item>
                            <Descriptions.Item label="final_label">
                              {formatPrimitive(segment.final_label)}
                            </Descriptions.Item>
                            <Descriptions.Item label="pre_label">{formatPrimitive(segment.pre_label)}</Descriptions.Item>
                            <Descriptions.Item label="auto_label">{formatPrimitive(segment.auto_label)}</Descriptions.Item>
                          </Descriptions>
                        ))}
                      </Space>
                    </Card>
                  )}

                  {result.clustering_debug && (
                    <Collapse
                      size="small"
                      items={[
                        {
                          key: 'clustering-debug-raw',
                          label: '生 JSON を表示',
                          children: (
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(result.clustering_debug, null, 2)}
                            </pre>
                          ),
                        },
                      ]}
                    />
                  )}
                </Space>
              </Card>
            )}

            {result.job_meta && (
              <Card size="small" title="job_meta.json">
                <Collapse
                  size="small"
                  items={[
                    {
                      key: 'job-meta-raw',
                      label: '生 JSON を表示',
                      children: (
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {JSON.stringify(result.job_meta, null, 2)}
                        </pre>
                      ),
                    },
                  ]}
                />
              </Card>
            )}
          </Space>
        )}
      </Card>
    </Space>
  );
};

export default AsrPlaygroundPage;
